import PropTypes from 'prop-types';
import { useMemo, useEffect, useReducer, useCallback } from 'react';

import axios, { endpoints } from 'src/utils/axios';

import { AuthContext } from './auth-context';
import { setSession, isValidToken } from './utils';

// ----------------------------------------------------------------------
/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */
// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  if (action.type === 'UPDATE_USER') {
    return {
      ...state,
      user: {
        ...state.user,
        ...action.payload,
      },
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const response = await axios.get(endpoints.auth.me);
        const user = response.data
        
        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...user,
              accessToken,
            },
          },
        });

      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (usuario, clave) => {
    const data = {
      usuario,
      clave,
      canal: "WEB",
    };

    const response = await axios.post(endpoints.auth.login, data);

    const { token } = response.data;
    const user = response.data;
    delete user.token;
    const accessToken = token;

    setSession(accessToken);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          ...user,
          accessToken,
        },
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (data = {}) => {
    
    await axios.post(endpoints.auth.register, data);

    dispatch({
      type: 'INITIAL',
      payload: {
        user: null,
      },
    });

  }, []);

  // LOGOUT
  const logout = useCallback(async () => {

    try {
      await axios.post(endpoints.auth.logout(state.user.usuario));
      setSession(null);

      // Limpiar completamente sessionStorage y localStorage para PWA
      sessionStorage.clear();
      localStorage.clear();

      // Limpiar cache de la aplicación si está en PWA
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      dispatch({
        type: 'LOGOUT',
      });
    }
    catch (error) {
      console.error('Error: ',  error);

      // Asegurar limpieza incluso si hay error
      sessionStorage.clear();
      localStorage.clear();

      dispatch({
        type: 'LOGOUT',
      });
    }

  }, [state]);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (usuario) => {
    const data = {
      usuario,
    }
    const response = await axios.post(endpoints.auth.resetPassword, data);
    /* if (response.data.c) {
      console.error(error);
      throw error;
    } */
  }, []);

  // UPDATE COMPANY
  const updateCompany = useCallback(async (data)=> {
      const response = await axios.put(endpoints.user.updateCompany, data)
      return response
  }, [])

  // UPDATE USER (local, in-memory)
  const updateUser = useCallback((data) => {
    dispatch({
      type: 'UPDATE_USER',
      payload: data,
    });
  }, []);
  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      forgotPassword,
      updateCompany,
      updateUser,
    }),
    [forgotPassword, login, logout, register, updateCompany, updateUser, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
